import { BOSS, FIXED_DT } from './balance'
import type { AttackDef, Boss, BossString, Player } from './types'

const trialMode = import.meta.env.DEV && typeof window !== 'undefined'
  ? new URLSearchParams(window.location.search).get('awokenFinale') : null
const farTrial = trialMode === 'reachFar' || trialMode === 'reachFarBefore'
const reachTrial = trialMode === 'reach' || farTrial
const reachBites = new Set(['feralBite', 'feralBiteDelayed', 'feralFrenzy', 'feralFrenzyExtend'])

function inFinale(boss: Pick<Boss, 'id' | 'executesTaken'>, enabled: boolean): boolean {
  return enabled && boss.id === 'crowned-king-awoken' && boss.executesTaken === 2
}

export function awokenFinaleRange(boss: Pick<Boss, 'id' | 'executesTaken'>, enabled = reachTrial, far = farTrial) {
  return inFinale(boss, enabled) ? { preferredRange: far ? 320 : 260, attackCommitRange: far ? 330 : 270 } : undefined
}

/** 遠間だけ上限を拡張。密着時の最低前進量を増やさず、口の当たり範囲も変更しない。 */
export function awokenFinaleLunge(boss: Boss, player: Player, attack: AttackDef, enabled = reachTrial, far = farTrial): number {
  const base = attack.lungeDistance ?? 0
  return inFinale(boss, enabled) && reachBites.has(attack.id) && Math.abs(boss.x - player.x) > 210
    ? base + (far ? 120 : 60) : base
}

/** 遠間の目標は変えず、振りの前半でそこまで踏み込む。現在のplayer位置は追わない。 */
export function awokenFinaleLungeSpeed(boss: Boss, attack: AttackDef, enabled = trialMode === 'reachFar'): number {
  const distance = Math.abs(boss.lungeTargetX - boss.actionAnchorX)
  if (!inFinale(boss, enabled) || !reachBites.has(attack.id)
      || distance <= (attack.lungeDistance ?? 0)) return BOSS.lungeSpeed
  // 起点と目標はactive突入時の固定値。途中で減速したり、背後への回避を追い直したりしない。
  const travelSec = Math.max(FIXED_DT, attack.activeMs * 0.5 / 1000)
  return Math.max(BOSS.lungeSpeed, distance / travelSec)
}

/** 床技を従来の交戦距離の外から出さない。予告の形と逃げ道は従来のまま。 */
export function awokenFinaleStrings(boss: Boss, player: Player, strings: BossString[], enabled = reachTrial): BossString[] {
  if (!inFinale(boss, enabled) || Math.abs(boss.x - player.x) <= 210) return strings
  return strings.filter(string => !string.beats.some(beat => beat.attackId === 'rampage'))
}

/** DEV比較の幕3構成。各技の時間・判定・絵と、幕1/2の選択は維持する。 */
export function buildAwokenFinaleTrial(strings: BossString[]): BossString[] {
  const earlyOnly = new Set(['ak-bite', 'ak-bite-bite', 'ak-frenzy', 'ak-bite-delay'])
  return strings.map(string => {
    const copy = { ...string, beats: string.beats.map(beat => ({ ...beat })) }
    if (earlyOnly.has(copy.id)) copy.maxExecutes = 1
    if (copy.id === 'ak-wall-rush') copy.weight = 2.4
    if (copy.id === 'ak-bite-bite-rush') copy.weight = 1.1
    return copy
  })
}
